import { useState, useEffect, useLayoutEffect, useRef } from "react";

/**
 * Drives the "scroll through the whole week" day view: tracks which day section
 * is under the sticky header (scroll-spy), measures the sticky region so day
 * anchors land just below it, and exposes a tap-to-scroll helper that briefly
 * suppresses the spy during the animation.
 *
 * Returns `sectionRefs` (the caller wires each day section into it) plus
 * `stickyRef` for the sticky region, the `activeDay` ISO date, the measured
 * `sectionOffset`, and `scrollToDay`.
 */
export default function useClassScrollSpy({ dayViewActive, nonEmptyDays, classesByDate, pendingDay, clearPendingDay }) {
  const [activeDay, setActiveDay] = useState(null); // date currently in view
  const [sectionOffset, setSectionOffset] = useState(220);

  const stickyRef = useRef(null);
  const sectionRefs = useRef({}); // dateISO -> section element
  const isClickScrolling = useRef(false);
  const clickTimer = useRef(null);

  const nonEmptyKey = nonEmptyDays.join(",");

  // Keep the active day valid; default to the first day that has classes.
  useEffect(() => {
    if (!nonEmptyDays.length) {
      setActiveDay(null);
      return;
    }
    if (!nonEmptyDays.includes(activeDay)) setActiveDay(nonEmptyDays[0]);
  }, [nonEmptyKey, activeDay]);

  // Measure the sticky header so day-section anchors land just below it.
  useLayoutEffect(() => {
    if (!dayViewActive) return undefined;
    const measure = () => {
      const h = stickyRef.current?.offsetHeight || 0;
      setSectionOffset(64 + h + 8); // 64 = app bar height
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (stickyRef.current) ro.observe(stickyRef.current);
    return () => ro.disconnect();
  }, [dayViewActive, nonEmptyDays.length]);

  // Scroll-spy: highlight the day-strip pill for the day section under the
  // sticky header. Picks the last day whose top has scrolled up past a detection
  // line just below the sticky region. A fixed line is used instead of an
  // IntersectionObserver band because the band collapsed to zero height when the
  // expanded filter panel pushed sectionOffset past the viewport's mid-point,
  // which silently stopped the spy from updating on scroll.
  useEffect(() => {
    if (!dayViewActive || nonEmptyDays.length === 0) return undefined;
    const onScroll = () => {
      if (isClickScrolling.current) return;
      const line = sectionOffset + 8;
      let next = nonEmptyDays[0];
      for (const d of nonEmptyDays) {
        const el = sectionRefs.current[d];
        if (!el) continue;
        if (el.getBoundingClientRect().top - line <= 1) next = d;
        else break;
      }
      // At the page bottom the last day may never cross the line; pin it.
      if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2) {
        next = nonEmptyDays[nonEmptyDays.length - 1];
      }
      setActiveDay(next);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [dayViewActive, nonEmptyKey, sectionOffset]);

  const scrollToDay = (d) => {
    if ((classesByDate.get(d) || []).length === 0) return;
    setActiveDay(d);
    const el = sectionRefs.current[d];
    if (!el) return;
    isClickScrolling.current = true;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    clearTimeout(clickTimer.current);
    clickTimer.current = setTimeout(() => {
      isClickScrolling.current = false;
    }, 700);
  };

  // A jump asked for a specific day, and the week it lives in has now loaded.
  // Land on it rather than on whatever the week happens to start with — and on
  // the next day that HAS classes when the day itself is empty, since an exact
  // hit is not what the client is after, the first session they can use is.
  useEffect(() => {
    if (!pendingDay) return;
    if (nonEmptyDays.length === 0) return; // the week is still loading
    const target = nonEmptyDays.includes(pendingDay) ? pendingDay : nonEmptyDays.find((d) => d >= pendingDay);
    if (target) scrollToDay(target);
    clearPendingDay?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingDay, nonEmptyKey]);

  return { stickyRef, sectionRefs, activeDay, sectionOffset, scrollToDay };
}
